import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { UserRole, type User } from '@hms/shared';
import { hospitalsApi } from '@/services/api/hospitalsApi';
import { looksLikeUuid } from '@/utils/uuid';

/**
 * Hospital shape used by the active-hospital context. Just identity + country
 * — currency moved to the organization in migration 20260519000001, so it
 * lives in `organizationContext` now. The full Hospital model (VAT, contact
 * info, etc.) is still fetched separately when needed.
 */
export interface HospitalSummary {
  id: string;
  name: string;
  /** ISO-3166 alpha-2 country code (e.g. 'PT', 'AO', 'MZ'). Empty when not yet loaded from /hospitals/mine. */
  country: string;
  /** IANA time zone of the hospital's wall clock. Missing until /hospitals/mine answers (and in older saved sessions). */
  timeZone?: string;
}

/**
 * A hospital's name for display, or null while it is unknown: before /hospitals/mine answers an entry has no
 * name (and one saved by an older session may hold its id as the name). A hospital id is never shown.
 */
export const knownHospitalName = (hospital: Pick<HospitalSummary, 'id' | 'name'> | null | undefined): string | null =>
  hospital?.name && hospital.name !== hospital.id && !looksLikeUuid(hospital.name) ? hospital.name : null;

// Roles that may send `X-Hospital-Id: ALL` (i.e. "All Hospitals" mode).
const ROLES_WITH_ALL_ACCESS: ReadonlySet<string> = new Set([UserRole.SUPER_ADMIN, UserRole.ADMIN]);

/**
 * Active-hospital context store — see multi-tenancy-spec §5.2.
 *
 * Tracks which hospital the current user is "acting on". Persisted to
 * localStorage via pinia-plugin-persistedstate so a page reload keeps the
 * selection. The active-hospital axios interceptor in `config/axios.ts`
 * reads from this same storage to attach the X-Hospital-Id header.
 */
export const useHospitalContextStore = defineStore(
  'hospitalContext',
  () => {
    // null = "All Hospitals" (only valid for SUPER_ADMIN / ADMIN).
    const activeHospitalId = ref<string | null>(null);
    const availableHospitals = ref<HospitalSummary[]>([]);

    const hasActiveHospital = computed(() => activeHospitalId.value !== null);

    const activeHospital = computed(() => {
      if (!activeHospitalId.value) return null;
      return availableHospitals.value.find((h) => h.id === activeHospitalId.value) ?? null;
    });

    const setActive = (id: string | null) => {
      activeHospitalId.value = id;
    };

    const setAvailableHospitals = (hospitals: HospitalSummary[]) => {
      availableHospitals.value = hospitals;
    };

    /**
     * Bootstrap the store from the authenticated user. Called from the auth
     * store after a successful login and after session rehydration.
     *
     * Seeds availableHospitals with the user's hospitals right away (keeping the
     * names already known from the last session, the rest without a name), then
     * fetches the real hospital objects from `/hospitals/mine` to fill them in.
     */
    const initFromUser = (user: User | null) => {
      if (!user) {
        reset();
        return;
      }

      const hospitalIds = user.hospitalIds ?? [];

      const knownHospitals = new Map(availableHospitals.value.filter((hospital) => knownHospitalName(hospital)).map((hospital) => [hospital.id, hospital]));
      availableHospitals.value = hospitalIds.map((id) => knownHospitals.get(id) ?? { id, name: '', country: '' });

      // Auto-select rules:
      //   - exactly one hospital → pick it
      //   - admin / super-admin → default to "All"
      //   - multi-hospital doctor → pick the first (JWT orders primary first)
      //   - no hospitals (and not admin) → null
      if (hospitalIds.length === 1) {
        activeHospitalId.value = hospitalIds[0]!;
      } else if (ROLES_WITH_ALL_ACCESS.has(user.role)) {
        activeHospitalId.value = null;
      } else if (hospitalIds.length > 1) {
        activeHospitalId.value = hospitalIds[0]!;
      } else {
        activeHospitalId.value = null;
      }

      // Fire-and-forget: fill in the real hospital names.
      // Patients are skipped here — their JWT carries no hospitalIds so the
      // placeholder pass produced an empty list anyway, and most patient
      // pages don't need the hospital roster. The booking modal calls
      // loadAvailableHospitals() on open when it finds the list empty.
      if (user.role !== UserRole.PATIENT) {
        void loadAvailableHospitals();
      }
    };

    /**
     * Fetch the caller's authorized hospitals from `/hospitals/mine` and
     * replace `availableHospitals` with real {id, name} records. Called on
     * login and on session rehydration so the top-bar selector, subtitle
     * indicators, and admin forms all show proper hospital names instead
     * of raw UUIDs.
     */
    const loadAvailableHospitals = async (): Promise<void> => {
      try {
        const hospitals = await hospitalsApi.getMine();
        availableHospitals.value = hospitals.map((h) => ({
          id: h.id,
          name: h.name,
          country: h.country,
          timeZone: h.timeZone,
        }));
      } catch (error) {
        // Non-fatal: the entries from initFromUser keep the UI functional (selector + scope
        // filter still work); unknown names show as a neutral placeholder, never as ids.
        console.warn('Failed to load /hospitals/mine — falling back to placeholder names', error);
      }
    };

    const reset = () => {
      activeHospitalId.value = null;
      availableHospitals.value = [];
    };

    return {
      activeHospitalId,
      availableHospitals,
      hasActiveHospital,
      activeHospital,
      setActive,
      setAvailableHospitals,
      initFromUser,
      loadAvailableHospitals,
      reset,
    };
  },
  {
    persist: {
      paths: ['activeHospitalId', 'availableHospitals'],
    },
  },
);
